import { useState, useRef } from "react";
import { Store, Calendar, Phone, FileText, Send, Loader2, CheckCircle, Camera, Video, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

export default function EmprendedoresPage() {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    phone: "",
    visit_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (url.startsWith('/uploads/')) {
      return `${backendUrl}/api${url}`;
    }
    return `${backendUrl}${url}`;
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 10) {
      toast.error("Máximo 10 fotos permitidas");
      return;
    }

    setUploading(true);
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('title', file.name);

        const response = await axios.post(`${API}/media/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newPhotos.push({
          media_id: response.data.media_id,
          url: response.data.url,
          filename: response.data.filename
        });
      } catch (error) {
        console.error("Error uploading photo:", error);
        toast.error(`Error al subir ${file.name}`);
      }
    }

    setPhotos([...photos, ...newPhotos]);
    setUploading(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (videos.length + files.length > 3) {
      toast.error("Máximo 3 videos permitidos");
      return;
    }

    setUploading(true);
    const newVideos = [];

    for (const file of files) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name} es muy grande (máx 100MB)`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('title', file.name);

        const response = await axios.post(`${API}/media/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newVideos.push({
          media_id: response.data.media_id,
          url: response.data.url,
          filename: response.data.filename
        });
      } catch (error) {
        console.error("Error uploading video:", error);
        toast.error(`Error al subir ${file.name}`);
      }
    }

    setVideos([...videos, ...newVideos]);
    setUploading(false);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.phone || !formData.visit_date) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/entrepreneur-request`, {
        ...formData,
        photo_ids: photos.map(p => p.media_id),
        video_ids: videos.map(v => v.media_id),
      });
      setSubmitted(true);
      toast.success("¡Solicitud enviada correctamente!");
    } catch (error) {
      toast.error("Error al enviar la solicitud. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", phone: "", visit_date: "" });
    setPhotos([]);
    setVideos([]);
    setSubmitted(false);
  };

  return (
    <Layout>
      <PageHero
        section="emprendedores"
        title="Emprendedores"
        subtitle="¿Tienes un emprendimiento y quieres que te visitemos? Completa el formulario y nos pondremos en contacto contigo."
        badge="Solicita una visita"
        badgeIcon={Store}
      />

      <section className="py-12 md:py-16" data-testid="entrepreneur-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-brand-text mb-4">
                ¡Solicitud Enviada!
              </h2>
              <p className="text-brand-textSecondary mb-8">
                Hemos recibido tu solicitud de visita. Nos pondremos en contacto contigo pronto para coordinar los detalles.
              </p>
              <Button onClick={resetForm} variant="outline" className="rounded-full">
                Enviar otra solicitud
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Store className="h-8 w-8 text-purple-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-brand-text mb-2">
                  Solicita una Visita
                </h2>
                <p className="text-brand-textSecondary">
                  Queremos conocer tu emprendimiento y compartirlo con nuestra comunidad
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-2">
                    <FileText className="inline h-4 w-4 mr-2 text-gray-400" />
                    Nombre del Emprendimiento *
                  </label>
                  <Input
                    type="text"
                    placeholder="Ej: Panadería La Abuelita"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-xl"
                    data-testid="entrepreneur-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-text mb-2">
                    <FileText className="inline h-4 w-4 mr-2 text-gray-400" />
                    Descripción *
                  </label>
                  <Textarea
                    rows={4}
                    placeholder="Cuéntanos sobre tu emprendimiento, qué productos o servicios ofreces, tu historia..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="rounded-xl resize-none"
                    data-testid="entrepreneur-description"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      <Phone className="inline h-4 w-4 mr-2 text-gray-400" />
                      Teléfono / WhatsApp *
                    </label>
                    <Input
                      type="tel"
                      placeholder="+504 9999-9999"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="rounded-xl"
                      data-testid="entrepreneur-phone"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      <Calendar className="inline h-4 w-4 mr-2 text-gray-400" />
                      Fecha deseada para la visita *
                    </label>
                    <Input
                      type="date"
                      value={formData.visit_date}
                      onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                      required
                      className="rounded-xl"
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="entrepreneur-date"
                    />
                  </div>
                </div>

                {/* Photo Gallery */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-brand-text">
                    <Camera className="inline h-4 w-4 mr-2 text-gray-400" />
                    Fotos del Emprendimiento (opcional, máx. 10)
                  </label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-purple-300 transition-colors">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className="cursor-pointer">
                      {uploading ? (
                        <Loader2 className="h-8 w-8 mx-auto text-purple-500 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Haz clic para subir fotos</p>
                        </>
                      )}
                    </label>
                  </div>

                  {photos.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                          <img
                            src={getImageUrl(photo.url)}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Video Gallery */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-brand-text">
                    <Video className="inline h-4 w-4 mr-2 text-gray-400" />
                    Videos (opcional, máx. 3, hasta 100MB c/u)
                  </label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-purple-300 transition-colors">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      multiple
                      onChange={handleVideoUpload}
                      className="hidden"
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer">
                      {uploading ? (
                        <Loader2 className="h-8 w-8 mx-auto text-purple-500 animate-spin" />
                      ) : (
                        <>
                          <Video className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Haz clic para subir videos</p>
                        </>
                      )}
                    </label>
                  </div>

                  {videos.length > 0 && (
                    <div className="space-y-2">
                      {videos.map((video, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Video className="h-5 w-5 text-gray-400" />
                            <span className="text-sm truncate">{video.filename}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={loading || uploading}
                    className="w-full rounded-full py-6 bg-purple-600 hover:bg-purple-700"
                    data-testid="entrepreneur-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Enviar Solicitud
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Al enviar este formulario, aceptas que nos pongamos en contacto contigo.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
